/*
 * tc-nav-drawer.js: behaviour for the H5 mobile menu drawer (shopify-theme #561).
 *
 * Spec: docs/specs/2026-10-07-nav-redesign-design.md, section 3 (mobile) and P4.
 * Plan: docs/specs/2026-10-07-nav-redesign-plan-P4-mobile-drawer-h5.md.
 * Markup: snippets/tc-nav-drawer.liquid. Styles: assets/tc-nav-drawer.css.
 *
 * theme.js keeps doing what it already does for this drawer (Drawer, L9 c72769):
 * open and close, aria-expanded on the menu button, the scroll lock and the focus
 * hand-off to the close button and back to the menu button. Its focus trap
 * (trapFocus) is still set up, but its Tab handler no longer receives the key while
 * the drawer is open: see 6. This file adds what the H5 design asks for on top:
 *   1. the close button sits on the menu button's spot (topBarGeometry);
 *   2. a swipe toward the left edge closes (swipeDecision);
 *   3. Back closes: one history entry while the drawer is open, removed again on
 *      every other close, so Back never needs two presses (createHistoryGuard);
 *   4. the page behind the drawer is inert (inertTargets), except a third-party
 *      layer that has to stay usable on top of it (LIVE_LAYERS: Alia's popup);
 *   5. Clarity events for opening and for the close method (spec section 8);
 *   6. Tab stays the drawer's own key while it is open, whatever else on the page
 *      listens for it, and wraps at the two ends as theme.js's trap did (the window
 *      keydown listener in wireDocument);
 *   7. the phone bar's search icon (#581) opens this drawer with focus in its search
 *      field, and gets focus back when the drawer closes (aimFocus, and the click
 *      listener in wireDocument).
 * The dimmed strip and Escape close through theme.js's own listeners; every close
 * this file starts (the X, a swipe, Back, a link) asks theme.js to close
 * by dispatching the drawer's theme:drawer:close event.
 * The pure functions at the top are unit-tested in tests/nav/drawer-*.test.js. The
 * browser wiring below them is driven in a small DOM by
 * tests/nav/drawer-wiring.test.js and in Chrome by tests/nav/browser/p4-*.js.
 * Loaded with defer from sections/custom-header.liquid. module.exports in Node,
 * window.TCNavDrawer in the browser. No build step. ES5 syntax; it uses the DOM
 * features theme.js already needs (CustomEvent, Element.closest, URL, FormData).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.TCNavDrawer = api;
    api.start();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ENTRY = 'tcNavDrawer';
  var X_SIZE = 44;       // the close button is a 44px square
  var X_INSET = 6;       // (56px top bar - 44px button) / 2
  var STUCK_MAX = 16;    // a top bar this close to the screen top pins the panel to 0
  var SWIPE_MIN = 60;    // px toward the left edge that always closes
  var FLICK_MIN = 30;    // px that close when the move is fast
  var FLICK_SPEED = 0.4; // px per ms
  var DRAG_START = 8;    // px before a horizontal move counts as a drag
  var POP_WAIT = 1000;   // ms to wait for the popstate of a history.back() this file asked for
  var SKIP_TAGS = /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/;
  // Third-party layers that stay live while the drawer is open (never inert, and
  // no ancestor of one either). Alia's script adds its root, div#alia-root-<id>, to
  // the body a second or two into the page load, empty, and renders its sign-up
  // popup into it later (half a second to several seconds on), full screen at
  // z-index 2147483647. A menu opened in between made the root inert with the rest
  // of the page: the popup then covered the open menu and was dead. A tap on its
  // Close reached the dimmed strip, a tap on its buttons the menu's links. The old
  // drawer left it live, so that was a regression (independent QA gate and a11y
  // audit of PR #580, 2026-10-09: "Alia, order 2"). Ruling: it stays live and on
  // top, as on the live site, and is closed with its own Close; nothing here moves
  // it behind the menu. A layer that only mounts after the menu opened was never
  // in the inert set to begin with.
  var LIVE_LAYERS = ['[id*="alia-root"]'];
  // The phone bar's search icon (sections/custom-header.liquid).
  var SEARCH_OPENER = '[data-tc-drawer-search]';

  // ---------------------------------------------------------------- pure logic

  function isDrawerEntry(state) {
    return !!(state && typeof state === 'object' && state[ENTRY]);
  }

  // Back-button support. One entry is pushed when the drawer opens; Back pops it
  // and popped() answers 'close'. Every other close goes back over the entry
  // (closed()), but only while the current entry is provably this drawer's, so the
  // guard can never take the shopper off the page. A link or form in the drawer
  // replaces the entry instead (leaving()), so Back from the next page returns to
  // this page in one press.
  function createHistoryGuard(history, key) {
    var mode = 'idle'; // idle | pushed | popping
    var reopen = false;
    var ours = function (state) {
      return isDrawerEntry(state) && state[ENTRY] === key;
    };
    var push = function () {
      var state = {};
      state[ENTRY] = key;
      try {
        history.pushState(state, '');
        mode = 'pushed';
      } catch (error) {
        mode = 'idle';
      }
    };
    return {
      mode: function () {
        return mode;
      },
      // A drawer entry left by an earlier load of this page (a reload while the
      // drawer was open) becomes a plain entry.
      init: function () {
        if (isDrawerEntry(history.state) && !ours(history.state)) {
          try {
            history.replaceState(null, '');
          } catch (error) {
            // Keep the entry; Back from it is then a no-op once.
          }
        }
      },
      // This drawer's entry can still be the current one while the guard is idle: its
      // history.back() never reached this window (reset() after the wait), or the
      // page came back from the back/forward cache. It is this open's entry again;
      // a second one would cost a second Back press.
      opened: function () {
        if (mode === 'idle') {
          if (ours(history.state)) mode = 'pushed';
          else push();
        } else if (mode === 'popping') {
          reopen = true;
        }
      },
      // Returns 'back' (went back over the entry), 'left' (the entry is no longer
      // on top, so it is left alone) or 'none'.
      closed: function () {
        reopen = false;
        if (mode !== 'pushed') return 'none';
        if (!ours(history.state)) {
          mode = 'idle';
          return 'left';
        }
        mode = 'popping';
        history.back();
        return 'back';
      },
      // True when the caller may replace the current entry (location.replace).
      leaving: function () {
        var replaceable = mode === 'pushed' && ours(history.state);
        mode = 'idle';
        reopen = false;
        return replaceable;
      },
      // Called for every popstate. Returns 'swallow' (the pop this guard asked
      // for), 'close' (Back pressed while open), 'open' (Forward onto this
      // drawer's entry) or 'pass' (not the drawer's business).
      popped: function () {
        if (mode === 'popping') {
          mode = 'idle';
          if (reopen) {
            reopen = false;
            push();
          }
          return 'swallow';
        }
        if (mode === 'pushed' && !ours(history.state)) {
          mode = 'idle';
          return 'close';
        }
        if (mode === 'idle' && ours(history.state)) {
          mode = 'pushed';
          return 'open';
        }
        return 'pass';
      },
      reset: function () {
        mode = 'idle';
        reopen = false;
      }
    };
  }

  // A swipe toward the left edge closes; a mostly vertical move is a scroll and
  // never closes. dx and dy in px from the touch start, ms since the start.
  function swipeDecision(dx, dy, ms) {
    var ax = Math.abs(dx);
    if (dx >= 0 || ax < Math.abs(dy) * 1.5) return 'stay';
    if (ax >= SWIPE_MIN) return 'close';
    if (ax >= FLICK_MIN && ms > 0 && ax / ms >= FLICK_SPEED) return 'close';
    return 'stay';
  }

  // Whether a move so far is a horizontal drag that the panel should follow.
  function isHorizontalDrag(dx, dy) {
    return Math.abs(dx) > DRAG_START && Math.abs(dx) > Math.abs(dy) * 1.5;
  }

  // Where the top bar goes so the 44px close button sits exactly on the menu
  // button's icon, at the top of the page and with the header stuck (Kevin's rule:
  // the X sits where the menu button was). iconRect is the icon's
  // getBoundingClientRect(). In a 56px bar the X has 6px above it.
  //   Top of the page: the panel starts 6px above where the X belongs; a 56px bar
  //   (barPad 0).
  //   Header stuck near the top of the screen: the panel starts at 0 and the space
  //   above the X follows the icon. barPad is how much taller than 56px the bar
  //   is: 8 on the H5 board (a 64px bar), and negative, down to -6, when the icon is
  //   higher than a 56px bar allows. Since #581 the stuck phone header is the H5
  //   board's: a 56px capsule 8px from the top, its menu icon centred 36px down, so
  //   barPad is 8 and the bar 64px. (Before that it was a 44px flat bar with the
  //   icon centred about 21.6px down, and the X sat at the very top of a 50px bar.)
  //   The X never leaves the screen: an icon centred less than 22px from the top
  //   leaves it that much low.
  //   Icon centre above the screen (a header scrolled away): the plain 56px bar.
  function topBarGeometry(iconRect) {
    if (!iconRect || !(iconRect.width > 0) || !(iconRect.height > 0)) return null;
    var centre = iconRect.top + iconRect.height / 2;
    var xTop = Math.round(centre - X_SIZE / 2);
    var xLeft = Math.max(0, Math.round(iconRect.left + iconRect.width / 2 - X_SIZE / 2));
    var offset = xTop - X_INSET;
    if (offset >= STUCK_MAX) return { panelTop: offset, barPad: 0, xLeft: xLeft };
    if (centre < 0) return { panelTop: 0, barPad: 0, xLeft: xLeft };
    return { panelTop: 0, barPad: Math.max(-X_INSET, offset), xLeft: xLeft };
  }

  // What to make inert while the drawer is open: every sibling of the drawer and
  // of each of its ancestors, up to the children of stopAt (document.body).
  // Elements that are already inert are skipped, so closing undoes only what
  // opening did. keep (optional, the elements LIVE_LAYERS finds) stay live wherever
  // they sit: a kept element is never in the list, and neither is anything that
  // contains one. Such a container is opened up instead, and everything in it
  // beside the branch that leads to the kept element goes in the list.
  function inertTargets(el, stopAt, keep) {
    var live = [];
    var holders = [];
    for (var k = 0; keep && k < keep.length; k++) {
      live.push(keep[k]);
      for (var up = keep[k].parentNode; up; up = up.parentNode) holders.push(up);
    }
    var out = [];
    var take = function (candidate) {
      if (SKIP_TAGS.test(candidate.tagName || '')) return;
      if (candidate.hasAttribute && candidate.hasAttribute('inert')) return;
      if (live.indexOf(candidate) !== -1) return;
      if (holders.indexOf(candidate) === -1) {
        out.push(candidate);
        return;
      }
      var inside = candidate.children || [];
      for (var j = 0; j < inside.length; j++) take(inside[j]);
    };
    var node = el;
    while (node && node !== stopAt && node.parentNode) {
      var siblings = node.parentNode.children || [];
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] !== node) take(siblings[i]);
      }
      node = node.parentNode;
    }
    return out;
  }

  // ----------------------------------------------------------- browser wiring

  var current = null;
  var documentWired = false;

  function track(name) {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (error) {
      // Analytics never breaks the menu.
    }
  }

  function visible(c) {
    return c.drawer.classList.contains('drawer--visible');
  }

  function dispatch(c, type) {
    c.drawer.dispatchEvent(new CustomEvent(type, { bubbles: false }));
  }

  function menuButton(drawer) {
    var all = document.querySelectorAll('[data-drawer-toggle="hamburger"]');
    for (var i = 0; i < all.length; i++) {
      if (!drawer.contains(all[i])) return all[i];
    }
    return null;
  }

  function inLiveLayer(el) {
    return !!(el && el.closest && el.closest(LIVE_LAYERS.join(', ')));
  }

  function lockPage(c) {
    if (c.preview || c.inerted.length) return;
    var list = inertTargets(c.drawer, document.body, document.querySelectorAll(LIVE_LAYERS.join(', ')));
    for (var i = 0; i < list.length; i++) list[i].setAttribute('inert', '');
    c.inerted = list;
  }

  function releasePage(c) {
    for (var i = 0; i < c.inerted.length; i++) c.inerted[i].removeAttribute('inert');
    c.inerted = [];
  }

  // Where theme.js puts focus when the drawer has opened: Drawer.setFocus looks up
  // [data-first-focus] at that moment, and the snippet prints it on the close button.
  // For an open from the search icon the attribute moves to the search field (field),
  // so theme.js itself lands focus there, once; aimFocus(c, null) gives it back to the
  // close button. The close button stays [data-tc-drawer-close] throughout.
  function aimFocus(c, field) {
    if (!c.close || c.aim === field) return;
    if (c.aim) c.aim.removeAttribute('data-first-focus');
    if (field) {
      c.close.removeAttribute('data-first-focus');
      field.setAttribute('data-first-focus', '');
    } else {
      c.close.setAttribute('data-first-focus', '');
    }
    c.aim = field || null;
  }

  // The drawer's own search field (snippets/tc-nav-drawer.liquid, .tcd-search), or null
  // when the header's "Show search in header" is off.
  function searchField(c) {
    var holder = c.drawer.querySelector('.tcd-search');
    return holder ? holder.querySelector('input[type="search"]') : null;
  }

  // theme.js keeps aria-expanded on the menu button (its Drawer.buttons); the search
  // icon is not one of those, so it is kept in step here.
  function markOpeners(open) {
    var openers = document.querySelectorAll(SEARCH_OPENER);
    for (var i = 0; i < openers.length; i++) openers[i].setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function placeTopBar(c) {
    var button = c.button || menuButton(c.drawer);
    var icon = button && (button.querySelector('svg') || button);
    var g = icon ? topBarGeometry(icon.getBoundingClientRect()) : null;
    if (!g) return;
    c.drawer.style.setProperty('--tcd-panel-top', g.panelTop + 'px');
    c.drawer.style.setProperty('--tcd-bar-pad', g.barPad + 'px');
    c.drawer.style.setProperty('--tcd-x-left', g.xLeft + 'px');
    // The X is less than 6px from the top of the screen (the stuck header): the
    // sitewide focus ring reaches 6px out, so its top would be off screen. The
    // stylesheet draws the ring inside the X while this attribute is set.
    if (g.panelTop === 0 && g.barPad < 0) c.drawer.setAttribute('data-tcd-x-edge', '');
    else c.drawer.removeAttribute('data-tcd-x-edge');
  }

  // The controls Tab stops on inside the drawer, in order: focusable, drawn and not
  // hidden (the search field's reset button is visibility: hidden until there is
  // text). No positive tabindex is used in the drawer, so the order is the markup's.
  function tabStops(c) {
    var all = c.drawer.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      var index = el.getAttribute('tabindex');
      if (el.disabled || (index !== null && parseInt(index, 10) < 0)) continue;
      if (el.tagName === 'A' && !el.hasAttribute('href') && index === null) continue;
      if (el.tagName === 'INPUT' && el.getAttribute('type') === 'hidden') continue;
      if (!el.getClientRects().length) continue;
      if (window.getComputedStyle(el).visibility === 'hidden') continue;
      out.push(el);
    }
    return out;
  }

  // Every close this file starts goes through theme.js: the drawer's own
  // theme:drawer:close listener runs Drawer.hideDrawer.
  function close(c, method) {
    if (!visible(c)) return;
    c.method = method;
    releasePage(c);
    dispatch(c, 'theme:drawer:close');
  }

  // The wait for the popstate of the history.back() a close asked for is over: it
  // arrived (handlePopState), or it never will. history.back() fires no popstate in
  // this window when the newest entry belongs to an iframe that navigated while the
  // drawer was open; the guard would then swallow the shopper's next Back press.
  function settle(c) {
    window.clearTimeout(c.popTimer);
    c.popTimer = 0;
    if (c.guard && c.guard.mode() === 'popping') {
      c.guard.reset();
      // Reopened while waiting: the open drawer still needs its entry, or Back
      // would leave the page.
      if (visible(c) && !c.preview) c.guard.opened();
    }
  }

  // Theme editor only (shopify:block:select does not exist on the storefront):
  // shows the drawer so the merchandiser sees the block being edited. It goes
  // around theme.js on purpose. Drawer.showDrawer locks the page scroll and moves
  // focus to the close button, and the editor selects the block again after every
  // re-render, so each keystroke in the sidebar would lose the keyboard to the
  // preview. A preview moves no focus, traps none, locks nothing, leaves the page
  // live and does not slide.
  function preview(c) {
    c.preview = true;
    placeTopBar(c);
    c.drawer.classList.remove('display-none');
    c.drawer.classList.add('drawer--visible');
  }

  function onClassChange(c) {
    var open = visible(c);
    if (open === c.open) return;
    c.open = open;
    markOpeners(open);
    if (open) {
      if (c.preview) return;
      lockPage(c);
      if (c.guard) c.guard.opened();
      track('tc_menu_open');
      // theme.js moves focus to the close button on the panel's transitionend. If
      // that transition never ran (an interrupted open), send the event it waits
      // for; Drawer.setFocus is a once-listener, so a late duplicate is a no-op.
      // A popup in a live layer (LIVE_LAYERS) that arrived in the meantime and took
      // focus keeps it: it is the top layer now, and it hands focus back when it closes.
      window.clearTimeout(c.focusTimer);
      c.focusTimer = window.setTimeout(function () {
        var active = document.activeElement;
        if (visible(c) && !c.drawer.contains(active) && !inLiveLayer(active)) {
          c.drawer.dispatchEvent(new Event('transitionend'));
        }
      }, 600);
      return;
    }
    window.clearTimeout(c.focusTimer);
    releasePage(c);
    var method = c.method;
    var wasPreview = c.preview;
    c.method = '';
    c.preview = false;
    var went = c.guard ? c.guard.closed() : 'none';
    if (!wasPreview) track('tc_menu_close_' + (method || 'other'));
    // A link or the search has already sent the page on its way. Focusing the menu
    // button now would scroll a same-page target (Contact us on the contact page)
    // back out of view.
    if (method !== 'link' && method !== 'search') {
      var active = document.activeElement;
      if (c.button && (!active || active === document.body || c.drawer.contains(active))) c.button.focus();
    }
    // Opened from the search icon: focus goes back to that icon. Only from where a close
    // leaves it (on the menu button theme.js has just focused, on the body, or still in
    // the drawer), and not when the page is leaving.
    var opener = c.opener;
    c.opener = null;
    aimFocus(c, null);
    if (opener && method !== 'link' && method !== 'search') {
      var held = document.activeElement;
      if (!held || held === document.body || held === c.button || c.drawer.contains(held)) opener.focus();
    }
    if (went === 'back') {
      // handlePopState takes it from here once the pop lands.
      window.clearTimeout(c.popTimer);
      c.popTimer = window.setTimeout(function () {
        settle(c);
      }, POP_WAIT);
    }
  }

  // The drawer is going away (the theme editor re-rendered or removed the header
  // section): give the page back. Without this the discarded drawer's inert set was
  // never released, and the next drawer skipped it as "already inert".
  function retire(c) {
    window.clearTimeout(c.focusTimer);
    window.clearTimeout(c.popTimer);
    c.popTimer = 0;
    c.opener = null;
    aimFocus(c, null);
    releasePage(c);
    // theme.js took a scroll lock for this drawer and its Drawer.unload() is empty.
    if (c.open && !c.preview) c.panel.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    if (c.observer) c.observer.disconnect();
    if (c.guard) c.guard.reset();
    if (current === c) current = null;
  }

  function bind(drawer) {
    if (!drawer || drawer.__tcNavDrawer) return;
    var panel = drawer.querySelector('[data-tc-drawer-panel]');
    var scrim = drawer.querySelector('[data-drawer-underlay]');
    var closeButton = drawer.querySelector('[data-tc-drawer-close]');
    if (!panel || !scrim) return;
    var historyOn = drawer.getAttribute('data-tc-drawer-history') === 'on' && window.history && typeof window.history.pushState === 'function';
    var c = {
      drawer: drawer,
      panel: panel,
      scrim: scrim,
      close: closeButton,
      button: menuButton(drawer),
      opener: null, // the search icon that opened the drawer, until it closes
      aim: null,    // the search field while it carries data-first-focus
      guard: historyOn ? createHistoryGuard(window.history, String(Date.now()) + Math.random().toString(36).slice(2)) : null,
      inerted: [],
      open: false,
      preview: false,
      method: '',
      observer: null,
      focusTimer: 0,
      popTimer: 0,
      drag: null,
      suppressClick: false
    };
    if (current) retire(current);
    drawer.__tcNavDrawer = c;
    current = c;
    if (c.guard) c.guard.init();

    // #199, again for a drawer the theme editor re-rendered: the snippet's inline
    // copy only ran for the drawer the page loaded with (the #198 limitation).
    drawer.addEventListener('transitioncancel', function (evt) {
      if (evt.target !== drawer) evt.stopImmediatePropagation();
    });

    c.observer = new MutationObserver(function () {
      onClassChange(c);
    });
    c.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    if (closeButton) {
      closeButton.addEventListener('click', function (event) {
        event.preventDefault();
        close(c, 'x');
      });
    }

    drawer.addEventListener('click', function (event) {
      if (c.suppressClick) {
        c.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      var target = event.target;
      if (!target || !target.closest) return;
      if (event.defaultPrevented) return;
      // A modified click (a new tab or window) is the browser's business.
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var link = target.closest('a[href]');
      if (!link || !drawer.contains(link)) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      if (!/^https?:/i.test(link.href)) return;
      if (!c.guard || !c.guard.leaving()) return;
      event.preventDefault();
      close(c, 'link');
      window.location.replace(link.href);
    });

    drawer.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || event.defaultPrevented) return;
      if (String(form.getAttribute('method') || 'get').toLowerCase() !== 'get') return;
      if (!c.guard || !c.guard.leaving()) return;
      event.preventDefault();
      var url = new URL(form.action, window.location.href);
      new FormData(form).forEach(function (value, name) {
        url.searchParams.append(name, value);
      });
      close(c, 'search');
      window.location.replace(url.toString());
    });

    panel.addEventListener('pointerdown', function (event) {
      // A new press: the click it makes is its own, never the last drag's.
      c.suppressClick = false;
      if (event.pointerType === 'mouse' || !visible(c)) return;
      c.drag = { x: event.clientX, y: event.clientY, t: Date.now(), id: event.pointerId, active: false };
    });
    panel.addEventListener('pointermove', function (event) {
      var d = c.drag;
      if (!d || event.pointerId !== d.id) return;
      var dx = event.clientX - d.x;
      if (!d.active) {
        if (!isHorizontalDrag(dx, event.clientY - d.y)) return;
        d.active = true;
        panel.style.transition = 'none';
      }
      panel.style.transform = 'translate3d(' + Math.min(0, dx) + 'px, 0, 0)';
    });
    var endDrag = function (event, cancelled) {
      var d = c.drag;
      if (!d || event.pointerId !== d.id) return;
      c.drag = null;
      if (!d.active) return;
      panel.style.transition = '';
      panel.style.transform = '';
      c.suppressClick = true;
      window.setTimeout(function () {
        c.suppressClick = false;
      }, 400);
      if (!cancelled && swipeDecision(event.clientX - d.x, event.clientY - d.y, Date.now() - d.t) === 'close') {
        close(c, 'swipe');
      }
    };
    panel.addEventListener('pointerup', function (event) {
      endDrag(event, false);
    });
    panel.addEventListener('pointercancel', function (event) {
      endDrag(event, true);
    });
  }

  // Document-level listeners, bound once per page. The three capture listeners
  // release the page BEFORE theme.js hides the drawer (capture runs before the
  // drawer's own listeners), so Drawer.hideDrawer's focus() on the menu button
  // does not land on an inert element.
  function wireDocument() {
    if (documentWired) return;
    documentWired = true;
    document.addEventListener('theme:drawer:open', function (event) {
      if (current && event.target === current.drawer) placeTopBar(current);
    }, true);
    document.addEventListener('theme:drawer:close', function (event) {
      if (current && event.target === current.drawer) releasePage(current);
    }, true);
    document.addEventListener('click', function (event) {
      var c = current;
      if (!c || !visible(c)) return;
      if (event.target === c.scrim || c.scrim.contains(event.target)) {
        c.method = 'scrim';
        releasePage(c);
      }
    }, true);
    document.addEventListener('keyup', function (event) {
      var c = current;
      if (!c || event.code !== 'Escape' || !visible(c)) return;
      c.method = 'escape';
      releasePage(c);
      // Something inside the drawer (predictive search) may keep it open: then this
      // Escape closed nothing, and the next close must not be logged as one.
      window.setTimeout(function () {
        if (!visible(c)) return;
        c.method = '';
        lockPage(c);
      }, 0);
    }, true);
    document.addEventListener('theme:resize', function () {
      if (current && visible(current)) placeTopBar(current);
    });
    // The phone bar's search icon (#581): the same drawer, opened by theme.js as the
    // menu button opens it, with focus aimed at the search field. A drawer with no
    // search field opens as usual, with focus on the close button.
    document.addEventListener('click', function (event) {
      var c = current;
      var target = event.target;
      var opener = target && target.closest ? target.closest(SEARCH_OPENER) : null;
      if (!opener || !c || c.preview || visible(c)) return;
      event.preventDefault();
      c.opener = opener;
      var field = searchField(c);
      aimFocus(c, field);
      dispatch(c, 'theme:drawer:open');
      // Only when theme.js took the request is display-none off. Before theme.js has connected
      // its Drawer (it does that on DOMContentLoaded, and this file binds when it runs) nothing
      // takes the request and the menu stays shut: the aim and the opener set above are undone,
      // or the next open from the menu button would put focus in the search field and give it
      // back to this icon.
      if (c.drawer.classList.contains('display-none')) {
        c.opener = null;
        aimFocus(c, null);
        return;
      }
      // The cursor goes in the field inside this tap, so the phone raises its keyboard: iOS
      // Safari and Android Chrome allow a scripted focus() only in the call stack of the
      // user's tap. Drawer.showDrawer (theme.js) takes display-none off at once but adds
      // drawer--visible in a 1ms timer, and the stylesheet keeps the panel visibility: hidden
      // until that class is there, and a hidden field takes no focus. So the class is added
      // here too, after a reflow so the slide still runs from its closed state, and the
      // field is focused. theme.js adds the class again 1ms later (no change), onClassChange
      // runs once for the one change, and Drawer.setFocus lands on this same field later
      // (aimFocus). Without theme.js's Drawer the class would lock the page behind a menu
      // that is not drawn: that case returned above.
      if (field) {
        void c.panel.offsetWidth;
        c.drawer.classList.add('drawer--visible');
        field.focus({ preventScroll: true });
      }
    });
    // The menu's search, its clear button (#585). theme.js's handler for the form's reset
    // (HeaderSearchForm.onFormReset) empties the field, hides the X (the is-hidden class)
    // and focuses the field, but only when shouldResetForm() holds, and that looks across
    // the whole page for a selected link. The home page's tabs have one, so there the field
    // still empties (PredictiveSearch.closeResults) but the X stays and keeps focus. This
    // runs on the document, after the form's own listeners (reset bubbles), and finishes
    // only what the theme left undone: an empty field with the X still shown. Where the
    // theme acted it finds nothing to do. The desktop search panel's form is outside the
    // menu and is left to the theme. A menu that is not open (sliding shut, or shut) is left
    // alone too: a tap on the X as it slides away must not pull focus into its field.
    document.addEventListener('reset', function (event) {
      var c = current;
      var form = event.target;
      var holder = c && c.drawer.querySelector('.tcd-search');
      if (!holder || !form || !holder.contains(form) || !visible(c)) return;
      var field = searchField(c);
      var x = form.querySelector('button[type="reset"]');
      if (!field || !x || field.value !== '' || x.classList.contains('is-hidden')) return;
      x.classList.add('is-hidden');
      if (document.activeElement !== field) field.focus();
    });
    // Tab belongs to the open drawer. A script that listens for Tab on the document
    // and moves focus itself would send it into the page behind, which is inert: the
    // key then does nothing at all. Avada's cookie card does exactly that for every
    // Tab while the card shows (#395), and Tab was dead inside the menu. On the
    // window, in the capture phase, this listener runs before any document listener
    // and stops the key there. It does not cancel it: the browser moves focus to the
    // next control as it always does. theme.js's own trap (trapFocus) is a document
    // listener too and no longer sees the key, so the one thing it did is done here:
    // at the two ends focus wraps, and only there is the key cancelled, as theme.js
    // did. Left alone: a modified Tab, a preview in the theme editor, and a Tab whose
    // target is outside the drawer: a live layer (LIVE_LAYERS: Alia's popup keeps Tab
    // inside itself with its own document listener) or a layer that arrived after
    // the menu opened. Focus there means that layer is on top; its keys are its own.
    // The key stops at the window, so a control inside the drawer that needs its own Tab
    // keydown will not receive it.
    window.addEventListener('keydown', function (event) {
      var c = current;
      if (!c || c.preview || !visible(c)) return;
      if (event.key !== 'Tab' && event.code !== 'Tab') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      var active = document.activeElement;
      var nowhere = !active || active === document.body || active === document.documentElement;
      if (!nowhere && !c.drawer.contains(active)) return;
      event.stopPropagation();
      var stops = tabStops(c);
      if (!stops.length) return;
      if (event.shiftKey && (active === stops[0] || active === c.panel || active === c.drawer)) {
        event.preventDefault();
        stops[stops.length - 1].focus();
      } else if (!event.shiftKey && active === stops[stops.length - 1]) {
        event.preventDefault();
        stops[0].focus();
      }
    }, true);
    // A restore from the back/forward cache. Like a reload while the drawer was open
    // (createHistoryGuard.init), it can leave the drawer's entry as the current one:
    // the drawer is closed and the guard forgets the entry. The next Back press is
    // spent on it once, unless the menu is opened first: opened() takes the entry up
    // again and the close removes it.
    window.addEventListener('pageshow', function (event) {
      var c = current;
      if (!c || !event.persisted) return;
      if (c.guard) c.guard.reset();
      if (visible(c)) close(c, 'restore');
    });
    // The theme editor re-renders the header section without re-running its
    // inline scripts (#198): let go of the old drawer, bind the new one.
    document.addEventListener('shopify:section:unload', function (event) {
      var c = current;
      if (c && event.target && event.target.contains && event.target.contains(c.drawer)) retire(c);
    });
    document.addEventListener('shopify:section:load', function (event) {
      start(event.target);
    });
    // Theme editor: selecting a Menu block shows the drawer, so merchandisers see
    // the card, list or band they are editing (a preview, see preview()). Only in
    // the editor: a preview has none of the modal behaviour a shopper needs.
    document.addEventListener('shopify:block:select', function (event) {
      var c = current;
      if (!window.Shopify || !window.Shopify.designMode) return;
      if (c && c.drawer.contains(event.target) && !visible(c)) preview(c);
    });
  }

  // Called by the snippet's parse-time popstate listener (capture), which runs
  // before theme.js's own popstate listeners.
  function handlePopState(event) {
    var c = current;
    if (!c || !c.guard) return;
    var verdict = c.guard.popped();
    if (verdict === 'pass') return;
    event.stopImmediatePropagation();
    if (verdict === 'swallow') settle(c);
    else if (verdict === 'close' && visible(c)) close(c, 'back');
    else if (verdict === 'open' && !visible(c)) dispatch(c, 'theme:drawer:open');
  }

  function start(scope) {
    if (typeof document === 'undefined') return;
    var run = function () {
      wireDocument();
      var root = scope && scope.querySelector ? scope : document;
      bind(root.querySelector('[data-tc-drawer]'));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
  }

  return {
    ENTRY: ENTRY,
    isDrawerEntry: isDrawerEntry,
    createHistoryGuard: createHistoryGuard,
    swipeDecision: swipeDecision,
    isHorizontalDrag: isHorizontalDrag,
    topBarGeometry: topBarGeometry,
    inertTargets: inertTargets,
    handlePopState: handlePopState,
    start: start
  };
});
